import React, { useState, useEffect, useRef } from 'react';
import { Marker, Popup } from 'react-leaflet';
import { useHistory } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { centroid } from '@turf/turf';
import L from 'leaflet';

import { slugify } from '../../../utils';
import { useCtx } from '../../Context';
import { useTranslation } from '../../Context/Translations';
import { SubzoneType, TagType } from '../../../types';
import './Subzones.scss';
import CustomIcon from '../../Icons';

const subzoneMarkerIcon = (id: number): L.DivIcon =>
  L.divIcon({
    className: `subzone-marker-icon marker-icon--subzone marker-icon--subzone-${id}`,
    html: '',
    iconSize: [40, 50],
    iconAnchor: [20, 50]
  });

const Subzone = ({
  geo,
  title,
  subtitle,
  webaddress,
  slug,
  description,
  Media,
  Tags,
  id,
  i18n
}: SubzoneType): JSX.Element | null => {
  const { translate, currentLanguage } = useTranslation();
  const history = useHistory();
  const [activityTags, setActivityTags] = useState<TagType[]>([]);
  const { embed } = useCtx();
  const popupRef = useRef<Popup>(null);

  useEffect(() => {
    const tempTags: TagType[] = [];
    for (let i = 0; i < Tags.length; i++) {
      const splitted = Tags[i].title.split('_');
      if (splitted.length === 2) {
        if (splitted[0] === 'activity') {
          // To prevent modifying actual tags
          // Initial tempTag
          let tempTag: TagType = { Subzones: [], id: 0, title: '' };
          tempTag = Object.assign(tempTag, Tags[i]);
          tempTag.title = splitted[1];
          tempTags.push(tempTag);
        }
      }
    }
    setActivityTags(tempTags);
  }, [Tags]);

  if (!geo.coordinates) {
    return null;
  }

  let withPic = false;
  if (Media && Media.length !== 0) {
    withPic = true;
  }

  const buttonClick = (): void => {
    history.push(`/${slug}`);
  };
  const feat = centroid(geo).geometry;

  if (feat === null) {
    return null;
  }

  const coords: [number, number] = [feat.coordinates[1], feat.coordinates[0]];

  const popupOnOpen = () => {
    const popup = popupRef.current?.leafletElement as any;
    if (popup) {
      const map = popup._map;
      const px = map.project(popup._latlng);
      px.y -= popup._container.clientHeight / 1.75;
      map.panTo(map.unproject(px), { animate: true });
    }
  };

  // Fit popup to mobile devices, but add padding on larger screens
  /*const h = window.innerHeight;
    let padding: [number, number] = [30, 90];
    if (h <= 690) {
      padding = [0, 0];
    } else if (h > 690 && h <= 700) {
      padding = [0, 10];
    } else if (h > 700 && h <= 750) {
      padding = [20, 70];
    }*/

  return (
    <Marker
      id={`mapobject-${id}`}
      position={coords}
      icon={subzoneMarkerIcon(id)}
    >
      <Popup
        ref={popupRef}
        autoPan={false}
        offset={[0, -35]}
        autoPanPadding={[20, 20]}
        onOpen={popupOnOpen}
        className="subzone-popup"
      >
        <div className="subzone-popup__header">
          {withPic && Media ? (
            <img
              className="subzone-popup__image"
              alt={i18n[currentLanguage]?.title || title}
              src={`${process.env.REACT_APP_UPLOADS_URL}/${process.env.REACT_APP_ZONE_ID}/${Media[0].path}`}
            ></img>
          ) : null}

          <div className="subzone-popup__header-content">
            <h2 className="subzone-popup__title">
              {i18n[currentLanguage]?.title || title}
            </h2>
            <h3 className="subzone-popup__area">
              {i18n[currentLanguage]?.subtitle || subtitle}
            </h3>
            <div className="subzone-popup__tag-container">
              {activityTags.map((d: TagType, i: number) => {
                return (
                  <div
                    className={`category_icon category_icon--${slugify(
                      d.title
                    )}`}
                    key={`${i}tag-icon`}
                  >
                    <CustomIcon icon={slugify(d.title)} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="subzone-popup__description">
          <ReactMarkdown linkTarget="_blank">
            {i18n[currentLanguage]?.description || description}
          </ReactMarkdown>
        </div>
        {(i18n[currentLanguage]?.webaddress || webaddress) && embed ? (
          <a
            target="_top"
            rel="noopener noreferrer"
            className="subzone-popup__button"
            href={i18n[currentLanguage]?.webaddress || webaddress}
          >
            {translate('subzonepopup-button-text')}
          </a>
        ) : (
          <button className="subzone-popup__button" onClick={buttonClick}>
            {translate('subzonepopup-button-text')}
          </button>
        )}
      </Popup>
    </Marker>
  );
};

const Subzones = (): JSX.Element | null => {
  const { filteredSubzones } = useCtx();

  if (!filteredSubzones.length) {
    return null;
  }

  return (
    <>
      {filteredSubzones.map((s: SubzoneType) => (
        <Subzone key={s.id} {...s} />
      ))}
    </>
  );
};

export default Subzones;
